import ApiCall from "../common/ApiCall";
import { useDispatch } from "react-redux";
import { socialLogin } from "../features/auth/authSlice";
import { setTranslationCookie } from "../common/TranslationHelper";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "../auth/microsoftAuthConfig";

const useSocialLogin = () => {
  const dispatch = useDispatch();

  const createMsalInstance = async () => {
    try {
      const msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      return msalInstance;
    } catch (error) {
      console.error("MSAL initialization error:", error);
      throw new Error("Failed to initialize Microsoft authentication");
    }
  };

  const handleSocialLogin = async (provider, token) => {
    try {
      const result = await dispatch(socialLogin({ provider, token })).unwrap();
      if (result?.user?.native_language) {
        setTranslationCookie(result.user.native_language);
      }
      return { success: true };
    } catch (error) {
      console.error(`${provider} login error:`, error);
      return {
        success: false,
        message: error.message || "Login failed",
      };
    }
  };

  // Add Facebook login initialization
  const initFacebookSDK = () => {
    return new Promise((resolve) => {
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: process.env.REACT_APP_FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: "v18.0",
        });
        resolve();
      };

      // Load the Facebook SDK script
      (function (d, s, id) {
        var js,
          fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s);
        js.id = id;
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        fjs.parentNode.insertBefore(js, fjs);
      })(document, "script", "facebook-jssdk");
    });
  };

  const handleFacebookLogin = async () => {
    try {
      await initFacebookSDK();

      return new Promise((resolve, reject) => {
        window.FB.login(
          (response) => {
            if (response.authResponse) {
              const accessToken = response.authResponse.accessToken;
              resolve(accessToken);
            } else {
              reject(
                new Error("User cancelled login or did not fully authorize."),
              );
            }
          },
          { scope: "public_profile,email" },
        );
      });
    } catch (error) {
      console.error("Facebook SDK initialization error:", error);
      throw error;
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      const msalInstance = await createMsalInstance();

      // Clear any existing accounts first
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await msalInstance.setActiveAccount(accounts[0]);
      }

      // Attempt popup login
      const response = await msalInstance.loginPopup({
        ...loginRequest,
        prompt: "select_account", // Forces account selection
      });

      if (response.accessToken) {
        return response.accessToken;
      } else {
        throw new Error("No access token received from Microsoft");
      }
    } catch (error) {
      console.error("Microsoft login error:", error);

      // Handle specific error types
      if (
        error.errorCode === "popup_window_error" ||
        error.errorCode === "user_cancelled"
      ) {
        throw new Error("Login was cancelled or popup was blocked");
      } else if (error.errorCode === "consent_required") {
        throw new Error("Additional consent required");
      } else if (error.message?.includes("AADSTS50020")) {
        throw new Error(
          "Account not found in tenant. Please use a different account or contact administrator.",
        );
      } else {
        throw new Error("Microsoft login failed. Please try again.");
      }
    }
  };

  return { handleSocialLogin, handleFacebookLogin, handleMicrosoftLogin };
};

export default useSocialLogin;
